import { Position } from "@xyflow/react";
import type { NodeHandle } from "@xyflow/react";
import { CARD_WIDTH } from "./constants";
import { DEFAULT_CARD_HEIGHT } from "./layout";
import type { NodeGeometry } from "./layout";
import type { FlowNode } from "./types";

// eslint-disable-next-line import/no-unused-modules -- part of syncGraphNodes public API
export interface MeasuredSize {
  width?: number;
  height?: number;
}

const isComplete = (m: MeasuredSize | undefined): m is Required<MeasuredSize> =>
  m?.width !== undefined && m.height !== undefined;

/** Static handle declaration matching the `HiddenHandles` every node renders
 *  (1px target at top-center, 1px source at bottom-center). Declaring them on
 *  the user node makes React Flow derive `handleBounds` from the declaration
 *  on every nodes-prop sync instead of depending exclusively on DOM
 *  measurement state — so edges always have anchor points, even while a node
 *  is (re)measuring. Without this, a resync racing the measure cycle left
 *  nodes permanently uninitialized and their edges (the branch fans in the
 *  Task 10 QA report) were silently never rendered. */
const nodeHandles = (width: number, height: number): NodeHandle[] => [
  { type: "target", position: Position.Top, x: width / 2, y: 0 },
  { type: "source", position: Position.Bottom, x: width / 2, y: height },
];

/** Merges a freshly-built graph into the previous RF node state. The graph
 *  builder emits brand-new node objects with no `measured` data; React Flow
 *  only writes measurements through `onNodesChange` into our controlled state,
 *  so a resync that dropped them would reset RF's nodeLookup and restart the
 *  measure cycle (nodes stuck at visibility: hidden — the Task 10 QA bug).
 *
 *  `liveMeasured` (RF's internal nodeLookup measurements) takes precedence
 *  over the previous controlled state: RF writes a node's measurement into
 *  its internals SYNCHRONOUSLY at ResizeObserver time, while the matching
 *  dimension change reaches our state asynchronously via onNodesChange. A
 *  resync flushing inside that window would resubmit the node without
 *  `measured`, and React Flow's adoptUserNodes/parseHandles then resets the
 *  node's handleBounds — permanently, since the node observer never re-arms.
 *  Uninitialized nodes render their edges nowhere (getEdgePosition -> null),
 *  which is how the branch-fan connectors vanished in Task 10 QA round 2. */
export const syncGraphNodes = (
  prev: FlowNode[],
  graphNodes: FlowNode[],
  geometries: ReadonlyMap<string, NodeGeometry>,
  liveMeasured?: ReadonlyMap<string, MeasuredSize>,
): FlowNode[] => {
  const prevById = new Map(prev.map((node) => [node.id, node]));
  return graphNodes.map((node) => {
    const old = prevById.get(node.id);
    const geometry = geometries.get(node.id);
    const live = liveMeasured?.get(node.id);
    const measured = isComplete(live) ? live : old?.measured;

    // Loop groups are sized by the layout engine (header-only when collapsed,
    // content box when expanded) — stale DOM measurements from the expanded
    // state must not override the freshly computed geometry.
    if (node.type === "loopGroup" && geometry !== undefined) {
      const { width, height } = geometry;
      return {
        ...node,
        measured: { width, height },
        handles: nodeHandles(width, height),
        position: { x: geometry.x, y: geometry.y },
        style: { width, height },
      };
    }

    const width = measured?.width ?? geometry?.width ?? CARD_WIDTH;
    const height = measured?.height ?? geometry?.height ?? DEFAULT_CARD_HEIGHT;
    return {
      ...node,
      ...(measured !== undefined ? { measured } : {}),
      handles: nodeHandles(width, height),
      position:
        geometry !== undefined
          ? { x: geometry.x, y: geometry.y }
          : node.position,
    };
  });
};
